import { APP_CONFIG } from '@/lib/config/app-config';

export function getApiBaseUrl() {
  const fromEnv = String(import.meta.env?.VITE_API_BASE_URL || '').trim();
  return fromEnv || APP_CONFIG.DEFAULT_API_BASE_URL;
}

/**
 * Absolutize an API-relative path against the API base.
 *
 * Needed because the renderer is NEVER same-origin with the API — it runs on file:// when packaged
 * and on the Vite dev origin otherwise, and there is no dev proxy. Endpoints that hand back a path
 * for a plain <img> to load (the self-authenticating avatar URL, media links) therefore have to be
 * resolved explicitly; passing the raw path to a <img src> silently loads nothing.
 *
 * Absolute http(s)/data/blob values pass through untouched.
 */
export function resolveApiUrl(pathOrUrl) {
  const value = String(pathOrUrl || '').trim();
  if (!value) return '';
  if (/^(https?:|data:|blob:)/i.test(value)) return value;
  return `${getApiBaseUrl()}${value.startsWith('/') ? '' : '/'}${value}`;
}

/**
 * Stable slug for the ACTIVE API environment (protocol- and trailing-slash-insensitive), for
 * localStorage keys that reference server-side ids. Such ids only exist within one backend,
 * but the same app profile (and its localStorage) serves every environment — dev runs repoint
 * VITE_API_BASE_URL at localhost while packaged builds use the config default — so an id
 * remembered under one environment must never be read back under another. (Concretely: a
 * thread selected while pointed at dev used to be probed against staging on the next launch,
 * answering 403 "user is not a member of this thread" every time.)
 */
export function getApiEnvironmentKey() {
  return getApiBaseUrl()
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, '')
    .replace(/\/+$/, '');
}

export class ApiError extends Error {
  constructor(message, status, payload = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.payload = payload;
  }
}

// Fired on every 403 so the app shell can toast once and refresh permissions (see App.jsx).
// A window event rather than an import keeps this module dependency-free (no store cycles);
// the ApiError still throws, so every caller's local handling is untouched.
export const PERMISSION_DENIED_EVENT = 'healo-permission-denied';

// Fired on 401s from authenticated endpoints so the app shell can clear the (expired/invalid)
// session and land on the lock screen (see App.jsx). Login attempts and credential changes are
// excluded: their 401s can mean "wrong credentials for THAT request" and must never tear down
// the currently active session (e.g. every wrong PIN would otherwise log the active user out).
export const AUTH_EXPIRED_EVENT = 'healo-auth-expired';
const AUTH_EXPIRED_EXCLUDED_PATHS = new Set([
  '/api/login',
  '/api/login/pin',
  '/api/users/me/pin',
  '/api/users/me/password',
]);

export async function apiRequest(path, options = {}) {
  // suppressPermissionDeniedEvent: opt out of the global 403 toast for requests whose 403 is an
  // EXPECTED outcome the caller fully handles itself — e.g. probing a thread id restored from a
  // previous session that the user may no longer be a member of. The ApiError still throws.
  const { suppressPermissionDeniedEvent, ...fetchOptions } = options;
  const response = await fetch(`${getApiBaseUrl()}${path}`, fetchOptions);
  const payload = await response.json().catch(() => null);

  if (!response.ok) {
    if (response.status === 403 && !suppressPermissionDeniedEvent && typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(PERMISSION_DENIED_EVENT, { detail: { path } }));
    }
    if (
      response.status === 401 &&
      typeof window !== 'undefined' &&
      !AUTH_EXPIRED_EXCLUDED_PATHS.has(path)
    ) {
      // Carry the token the 401'd request was sent with: a stale 401 from the PREVIOUS
      // user's in-flight request (their token expiring is exactly when 401s appear) must
      // not tear down a freshly switched-to session — App.jsx compares this against the
      // currently active token before clearing.
      const authHeader = String(
        options?.headers?.Authorization || options?.headers?.authorization || '',
      );
      const requestToken = authHeader.replace(/^Bearer\s+/i, '').trim();
      window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT, { detail: { path, requestToken } }));
    }
    const message = payload?.error ? String(payload.error) : `request failed (${response.status})`;
    throw new ApiError(message, response.status, payload);
  }

  return payload;
}
